import {
  Stack,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Tabs,
  Container,
  SimpleGrid,
  ThemeIcon,
  Paper,
  Alert,
} from "@mantine/core";
import {
  IconCamera,
  IconTelescope,
  IconSettings,
  IconBolt,
  IconFilter,
  IconFocus,
  IconCpu,
  IconPhoto,
  IconInfoCircle,
} from "@tabler/icons-react";

// Type definitions
interface EquipmentItem {
  name: string;
  specs: string;
  primary?: boolean;
}

// Equipment data organized by category
const equipmentData = {
  mount: {
    title: "Mount & Control",
    icon: IconSettings,
    color: "blue",
    items: [
      {
        name: "ZWO AM5 Harmonic Drive Mount",
        specs: 'EQ/Alt-Az, <±10" PE',
        primary: true,
      },
      {
        name: "ZWO TC40 Carbon Fiber Tripod",
        specs: "Ultra-stable carbon fiber construction",
      },
      {
        name: "ZWO 200mm Pier Extension",
        specs: "Extended height for better clearance",
      },
      { name: "StellarMate Pro Controller", specs: "8GB RAM / 128GB storage" },
      {
        name: "StellarMate 10A Power Supply",
        specs: "US model, reliable power delivery",
      },
      {
        name: "Pegasus Astro Pocket Powerbox Advance Gen 2",
        specs: "Power + USB + dew control",
      },
    ],
  },
  telescopes: {
    title: "Telescopes & Optics",
    icon: IconTelescope,
    color: "violet",
    items: [
      {
        name: "Askar 120APO Triplet Refractor",
        specs: "120mm, 840mm, f/7",
        primary: true,
      },
      {
        name: "Askar 65PHQ Quintuplet Refractor",
        specs: "65mm, 416mm, f/6.4, flat-field",
        primary: true,
      },
      {
        name: "William Optics UniGuide 50mm Guide Scope",
        specs: 'with 1.25" RotoLock',
      },
    ],
  },
  cameras: {
    title: "Cameras",
    icon: IconCamera,
    color: "green",
    items: [
      {
        name: "ZWO ASI533MC Pro",
        specs: "Primary cooled OSC camera (Sony IMX533, 9MP)",
        primary: true,
      },
      { name: "ZWO ASI585MC", specs: "Primary guide cam or wide-field OSC" },
      { name: "ZWO ASI662MC", specs: "Lunar/planetary camera (small sensor)" },
      { name: "ZWO ASI676MC", specs: "Alternate imaging or lunar cam" },
    ],
  },
  imaging: {
    title: "Imaging Train & Filters",
    icon: IconFilter,
    color: "orange",
    items: [
      { name: "Askar M54 Filter Drawer", specs: 'accepts 2" filters' },
      {
        name: "IDAS NBZ-II Dual-Band Filter",
        specs: '10nm Ha, 8nm OIII, 2"',
        primary: true,
      },
      {
        name: "BlueFireBall M68-to-M54 Thread Adapter",
        specs: "for 120APO connection",
      },
    ],
  },
  focusing: {
    title: "Focusing & Dew Control",
    icon: IconFocus,
    color: "teal",
    items: [
      {
        name: "ZWO EAF Electronic Automatic Focuser",
        specs: "5V model with temp sensor and hand controller",
      },
      { name: "ZWO Anti-Dew Strip", specs: "for cooled cameras" },
    ],
  },
  power: {
    title: "Power & Connectivity",
    icon: IconBolt,
    color: "red",
    items: [
      {
        name: "Dedicated 12V power line",
        specs: "for AM5 mount (bypassing hub)",
      },
      {
        name: "Pegasus Powerbox distribution",
        specs: "to cameras, EAF, and dew strip",
      },
      {
        name: "StellarMate Pro control system",
        specs: "mount, cameras, focuser, and guiding (via KStars/Ekos/INDI)",
      },
    ],
  },
};

// Equipment component
function EquipmentSection() {
  return (
    <Stack gap="xl">
      <Stack gap="md" ta="center">
        <Title order={2} style={{ color: "#ffffff" }}>
          🔭 Master Rig Inventory
        </Title>
        <Text size="lg" style={{ color: "#d1d5db" }}>
          Professional-grade astrophotography equipment for capturing the cosmos
        </Text>
      </Stack>

      <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="lg">
        {Object.entries(equipmentData).map(([key, category]) => {
          const IconComponent = category.icon;
          return (
            <Card
              key={key}
              withBorder
              shadow="sm"
              h="100%"
              style={{
                backgroundColor: "rgba(30, 30, 30, 0.8)",
                borderColor: "rgba(139, 92, 246, 0.3)",
                /* backdropFilter: 'blur(disabled)' - DISABLED */
              }}
            >
              <Stack gap="md" h="100%">
                <Group gap="sm">
                  <ThemeIcon size="lg" variant="light" color={category.color}>
                    <IconComponent size={20} />
                  </ThemeIcon>
                  <Title order={4} style={{ color: "#ffffff" }}>
                    {category.title}
                  </Title>
                </Group>

                <Stack gap="sm" style={{ flex: 1 }}>
                  {category.items.map((item: EquipmentItem, index) => (
                    <Paper
                      key={index}
                      p="sm"
                      withBorder={!!item.primary}
                      bg={
                        item.primary ? "rgba(139, 66, 255, 0.1)" : "transparent"
                      }
                      style={{
                        borderColor: item.primary
                          ? `var(--mantine-color-${category.color}-6)`
                          : "rgba(139, 66, 255, 0.2)",
                      }}
                    >
                      <Stack gap={4}>
                        <Group justify="space-between" align="flex-start">
                          <Text
                            fw={item.primary ? 600 : 500}
                            size="sm"
                            style={{
                              flex: 1,
                              color: "#ffffff",
                            }}
                          >
                            {item.name}
                          </Text>
                          {item.primary && (
                            <Badge
                              size="xs"
                              variant="light"
                              color={category.color}
                            >
                              Primary
                            </Badge>
                          )}
                        </Group>
                        <Text
                          size="xs"
                          style={{
                            color: "#a1a1aa",
                          }}
                        >
                          {item.specs}
                        </Text>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            </Card>
          );
        })}
      </SimpleGrid>

      {/* Quick Stats */}
      <Card
        withBorder
        style={{
          backgroundColor: "rgba(30, 30, 30, 0.8)",
          borderColor: "rgba(139, 92, 246, 0.3)",
          /* backdropFilter: 'blur(disabled)' - DISABLED */
        }}
      >
        <Group justify="center" gap="xl">
          <Stack gap={4} align="center">
            <ThemeIcon size="xl" variant="light" color="blue">
              <IconTelescope size={24} />
            </ThemeIcon>
            <Text fw={600} size="lg" style={{ color: "#ffffff" }}>
              3
            </Text>
            <Text size="sm" style={{ color: "#a1a1aa" }}>
              Telescopes
            </Text>
          </Stack>
          <Stack gap={4} align="center">
            <ThemeIcon size="xl" variant="light" color="green">
              <IconCamera size={24} />
            </ThemeIcon>
            <Text fw={600} size="lg" style={{ color: "#ffffff" }}>
              4
            </Text>
            <Text size="sm" style={{ color: "#a1a1aa" }}>
              Cameras
            </Text>
          </Stack>
          <Stack gap={4} align="center">
            <ThemeIcon size="xl" variant="light" color="violet">
              <IconCpu size={24} />
            </ThemeIcon>
            <Text fw={600} size="lg" style={{ color: "#ffffff" }}>
              1
            </Text>
            <Text size="sm" style={{ color: "#a1a1aa" }}>
              AI Controller
            </Text>
          </Stack>
          <Stack gap={4} align="center">
            <ThemeIcon size="xl" variant="light" color="orange">
              <IconFilter size={24} />
            </ThemeIcon>
            <Text fw={600} size="lg" style={{ color: "#ffffff" }}>
              1
            </Text>
            <Text size="sm" style={{ color: "#a1a1aa" }}>
              Dual-Band Filter
            </Text>
          </Stack>
        </Group>
      </Card>
    </Stack>
  );
}

// Photo Gallery Component (placeholder for future implementation)
function PhotoGallerySection() {
  return (
    <Stack gap="xl">
      <Alert
        icon={<IconInfoCircle size={16} />}
        title="Photo Gallery Coming Soon"
        color="gray"
        style={{
          backgroundColor: "rgba(30, 30, 30, 0.8)",
          borderColor: "rgba(139, 92, 246, 0.3)",
        }}
      >
        <Text size="sm" style={{ color: "#d1d5db" }}>
          The astrophotography gallery is currently being developed. This
          section will showcase deep-sky objects, planetary captures, and
          wide-field shots taken with the equipment above.
        </Text>
        <Text size="sm" mt="sm" style={{ color: "#d1d5db" }}>
          <strong>Planned features:</strong>
        </Text>
        <Text
          size="sm"
          component="ul"
          style={{
            marginLeft: "1rem",
            marginTop: "0.5rem",
            color: "#d1d5db",
          }}
        >
          <li>Interactive image viewer with zoom and pan</li>
          <li>
            Detailed capture information (exposure times, filters, processing
            notes)
          </li>
          <li>
            Search and filter by object type, constellation, or equipment used
          </li>
          <li>Before/after processing comparisons</li>
          <li>Equipment correlation showing which gear captured each image</li>
        </Text>
      </Alert>

      <Card
        withBorder
        style={{
          backgroundColor: "rgba(30, 30, 30, 0.8)",
          borderColor: "rgba(139, 92, 246, 0.3)",
          /* backdropFilter: 'blur(disabled)' - DISABLED */
        }}
      >
        <Stack gap="md" align="center">
          <IconCamera size={48} color={"#8b5cf6"} />
          <Title order={3} style={{ color: "#ffffff" }}>
            Gallery Under Construction
          </Title>
          <Text ta="center" style={{ color: "#a1a1aa" }}>
            Check back soon for stunning astrophotography captures from the
            cosmos!
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}

export function AstrophotographyPage() {
  return (
    <Container size="xl">
      <Stack gap="xl">
        <Stack gap="md" ta="center">
          <Title order={1}>🌌 Astrophotography</Title>
          <Text
            c="dimmed"
            size="lg"
            style={{
              color: "#a1a1aa",
            }}
          >
            Capturing the beauty of the cosmos through long-exposure photography
            and digital processing.
          </Text>
        </Stack>

        <Tabs
          defaultValue="equipment"
          variant="pills"
          radius="md"
          styles={{
            tab: {
              color: "#ffffff",
              backgroundColor: "rgba(30, 30, 30, 0.8)",
              border: "1px solid rgba(139, 92, 246, 0.3)",
              "&:hover": {
                backgroundColor: "rgba(139, 92, 246, 0.2)",
                color: "#ffffff",
              },
              "&[data-active]": {
                backgroundColor: "rgba(139, 92, 246, 0.8)",
                color: "#ffffff",
                border: "1px solid rgba(139, 92, 246, 0.8)",
              },
            },
          }}
        >
          <Tabs.List grow>
            <Tabs.Tab
              value="equipment"
              leftSection={<IconSettings size={16} />}
            >
              Equipment & Rig
            </Tabs.Tab>
            <Tabs.Tab value="gallery" leftSection={<IconPhoto size={16} />}>
              Photo Gallery
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="equipment" pt="xl">
            <EquipmentSection />
          </Tabs.Panel>

          <Tabs.Panel value="gallery" pt="xl">
            <PhotoGallerySection />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
